import { TrendingUp, MapPin, Building2, Train, GraduationCap, Heart } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const reasons = [
  {
    icon: TrendingUp,
    title: "Rapid Economic Growth",
    description: "Solapur is experiencing significant industrial and economic development with new businesses and job opportunities",
    stats: "25% growth in IT sector"
  },
  {
    icon: Train,
    title: "Excellent Connectivity",
    description: "Strategic location with major railway junction connecting Mumbai, Pune, Bangalore, and Hyderabad",
    stats: "4-hour drive to Mumbai"
  },
  {
    icon: Building2,
    title: "Infrastructure Development",
    description: "Major infrastructure projects including metro connectivity, new airports, and smart city initiatives",
    stats: "₹5000 Cr investment approved"
  },
  {
    icon: GraduationCap,
    title: "Educational Hub",
    description: "Home to prestigious institutions and growing as an educational center in Maharashtra",
    stats: "50+ educational institutions"
  },
  {
    icon: Heart,
    title: "Quality of Life",
    description: "Perfect blend of urban amenities and traditional culture with affordable cost of living",
    stats: "30% lower cost than Mumbai"
  },
  {
    icon: MapPin,
    title: "Strategic Location",
    description: "Central location in Maharashtra providing easy access to major cities and business centers",
    stats: "200 km from Pune"
  }
];

const WhyInvestSolapur = () => {
const navigate = useNavigate();

  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    time: ""
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

   const validateForm = () => {
    const { name, email, phone, date, time } = formData;
    const phoneRegex = /^[6-9]\d{9}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!name || name.trim().length < 3) return "Please enter a valid name (min 3 characters)";
    if (!email || !emailRegex.test(email)) return "Please enter a valid email address";
    if (!phone || !phoneRegex.test(phone)) return "Please enter a valid 10-digit Indian phone number";
    if (!date) return "Please select a date";
    if (new Date(date) < new Date(new Date().setHours(0,0,0,0))) return "Date cannot be in the past";
    if (!time) return "Please select a time";

    return null; // all validations passed
  };


  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      setMessage(validationError);
      return;
    }

    setLoading(true);
    setMessage("");

    const API_URL = window.location.href.includes("localhost")
      ? process.env.REACT_APP_API_URL_LOCAL
      : process.env.REACT_APP_API_URL_PROD;

    try {
      const res = await fetch(`${API_URL}api/schedule-site-visit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (res.ok) {
        setMessage("Thank you! We will surely call you soon.");
        setFormData({ name: "", email: "", phone: "", date: "", time: "" });
        setTimeout(() => setModalOpen(false), 3000);
      } else {
        setMessage("Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error(error);
      setMessage("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  
  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16 animate-fade-up">
          <h2 className="font-display-bold text-4xl md:text-5xl text-primary mb-6">
            Why Invest in Solapur?
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Discover the compelling reasons that make Solapur one of Maharashtra's most promising investment destinations
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reasons.map((reason, index) => {
            const IconComponent = reason.icon;
            return (
              <div
                key={index}
                className="group p-8 rounded-xl bg-card border border-border hover:shadow-lg transition-all duration-300 animate-fade-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-luxury-muted rounded-lg flex items-center justify-center group-hover:bg-luxury group-hover:text-luxury-foreground transition-colors duration-300">
                    <IconComponent className="h-6 w-6" />
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="font-display text-xl text-primary mb-3 group-hover:text-luxury transition-colors">
                      {reason.title}
                    </h3>
                    
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      {reason.description}
                    </p>
                    
                    <div className="inline-block bg-primary/5 text-primary px-3 py-1 rounded-full text-sm font-medium">
                      {reason.stats}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-luxury/10 via-luxury/5 to-transparent rounded-2xl p-8 md:p-12">
            <h3 className="font-display-bold text-2xl md:text-3xl text-primary mb-4">
              Ready to Invest in Solapur's Future?
            </h3>
            <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
              Join thousands of smart investors who have already recognized Solapur's potential. 
              Start your real estate journey with Skyline Properties today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="btn-luxury px-8 py-3 rounded-md" onClick={() => navigate("/investment-options")}>
                Explore Investment Options
              </button>
             <button className="btn-outline-luxury px-8 py-3 rounded-md" onClick={() => setModalOpen(true)}>
                Schedule Site Visit
              </button>
            </div>
          </div>
        </div>
      </div>

       {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md relative">
            <h2 className="text-xl font-bold mb-4">Schedule Site Visit</h2>
            <input
              type="text"
              name="name"
              placeholder="Your Name"
              value={formData.name}
              onChange={handleChange}
              className="w-full border p-2 mb-3 rounded"
            />
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
              className="w-full border p-2 mb-3 rounded"
            />
            <input
              type="tel"
              name="phone"
              placeholder="Phone Number"
              value={formData.phone}
              onChange={handleChange}
              className="w-full border p-2 mb-3 rounded"
            />
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className="w-full border p-2 mb-3 rounded"
            />
            <input
              type="time"
              name="time"
              value={formData.time}
              onChange={handleChange}
              className="w-full border p-2 mb-3 rounded"
            />
            
            {message && <p className="text-sm text-green-600 mb-2">{message}</p>}

            <div className="flex justify-end gap-3 mt-4">
              <button
                className="px-4 py-2 bg-gray-200 rounded"
                onClick={() => setModalOpen(false)}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-primary text-white rounded"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? "Scheduling..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}

    </section>
  );
};

export default WhyInvestSolapur;